import { useState } from "react";
import { TENANT_DOMAIN } from "../../../utils/domain";
import { ERROR_MESSAGES, ITEMS_PER_PAGE } from "../../../constant/constant";
import { useAxios } from "../../../utils/useAxios";

/**
 * @typedef {Object} TeacherType
 * @property {number} id - The teacher ID
 * @property {string} tenant_id - The tenant UUID
 * @property {number} inst_id - The institution ID
 * @property {string} firstName - Teacher's first name
 * @property {string} lastName - Teacher's last name
 * @property {string} email - Teacher's email
 * @property {number} role_id - The role ID
 * @property {string} reg_type - Registration type
 * @property {string} status - Teacher status ('0' or '1')
 * @property {string} created_at - Creation timestamp
 * @property {string} updated_at - Update timestamp
 * @property {string} teacher_code - Unique teacher code
 */

/**
 * @typedef {Object} StudentPivot
 * @property {number} classroom_id - The classroom ID
 * @property {number} student_id - The student ID
 * @property {string} enrolled_at - Enrollment timestamp
 */

/**
 * @typedef {Object} StudentType
 * @property {number} id - The student ID
 * @property {string} tenant_id - The tenant UUID
 * @property {number} inst_id - The institution ID
 * @property {string} name - Student's full name
 * @property {string} username - Student's username
 * @property {number} is_pswd_reset - Password reset flag
 * @property {string|null} email - Student's email
 * @property {string} age - Student's age
 * @property {number} role_id - The role ID
 * @property {string} assigned_to - Assigned teacher ID
 * @property {string} createdBy - ID of the user who created the student
 * @property {string} class_id - Classroom ID
 * @property {string} status - Student status ('0' or '1')
 * @property {string} created_at - Creation timestamp
 * @property {string} updated_at - Update timestamp
 * @property {string} student_code - Unique student code
 */

/**
 * @typedef {Object} LessonType
 * @property {number} id - The lesson ID
 * @property {string} title - Lesson title
 * @property {string|null} description - Lesson description
 * @property {number} created_by - The ID of the user who created the lesson
 * @property {string} created_at - Creation timestamp
 * @property {string} updated_at - Update timestamp
 * @property {string} lesson_code - Unique lesson code
 */

/**
 * @typedef {Object} PaginatedStudents
 * @property {StudentType[]} data - Array of enrolled students
 * @property {number} current_page - Current page number
 * @property {number} last_page - Last page number
 * @property {number} per_page - Items per page
 * @property {number} total - Total number of students
 */

/**
 * @typedef {Object} ClassroomDetailType
 * @property {number} id - The classroom ID
 * @property {string} tenant_id - The tenant UUID
 * @property {number} teacher_id - The teacher's ID
 * @property {string} name - Classroom name
 * @property {string|null} description - Classroom description
 * @property {string|null} start_date - Start date in YYYY-MM-DD format
 * @property {string|null} end_date - End date in YYYY-MM-DD format
 * @property {string} status - Classroom status ('0' or '1')
 * @property {string} created_at - Creation timestamp
 * @property {string} updated_at - Update timestamp
 * @property {string} classroom_code - Unique classroom code
 * @property {TeacherType} teacher - The assigned teacher details
 * @property {PaginatedStudents} students - Paginated students data
 * @property {LessonType[]} lessons - Array of assigned lessons
 * @property {number } quiz_attempt_limit - Quiz attempt limit for the classroom
 */

/**
 * Student Data
 * @typedef {Object} SudentData
 * @property {Object[]} students - Array of enrolled students
 */

/**
 * @typedef {Object} GetClassroomByIdParams
 * @property {number} classroomId - Classroom ID (required)
 * @property {number} [page] - Page number for students pagination
 * @property {number} [per_page] - Number of students per page
 */

/**
 * Custom hook to fetch classroom detail by ID with pagination support for students
 */

export const useGetClassroomById = () => {
  const [classroom, setClassroom] = useState(
    /** @type {ClassroomDetailType | null | undefined} */(undefined),
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const { axiosInstance } = useAxios();

  /**
   * Fetch classroom details by ID with optional pagination for students
   * @async
   * @param {GetClassroomByIdParams} params
   * @returns {Promise<void>}
   */
  const getClassroomById = async ({
    classroomId,
    page = 1,
    per_page = ITEMS_PER_PAGE,
  } = {}) => {
    //TODO: Implement the pagination for students in update classroom and remove this per_page value
    per_page = 100;
    try {
      setLoading(true);
      setError("");

      // Build query params for pagination
      const query = new URLSearchParams();
      query.append("page", page);
      query.append("per_page", per_page);

      const url = `${TENANT_DOMAIN}/classroom/edit/${classroomId}?${query.toString()}`;

      const response = await axiosInstance.get(url);

      setClassroom(response?.data?.data || null);
    } catch (err) {
      setClassroom(null);
      if (err?.response?.status === 500) {
        setError(ERROR_MESSAGES.INTERNAL_SERVER);
      } else {
        setError(err?.response?.data?.message || ERROR_MESSAGES.UNKNOWN);
      }
    } finally {
      setLoading(false);
    }
  };

  return {
    classroom,
    loading,
    error,
    getClassroomById,
    setClassroom,
  };
};
